/**
 * Read-only supplier-orders panel for the Hospital Detail view.
 * Shows orders linked to this hospital + lets admin email status using the
 * same flow as SupplierOrdersManager.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Mail, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  open: "bg-amber-100 text-amber-800",
  submitted_to_supplier: "bg-blue-100 text-blue-800",
  backordered: "bg-orange-100 text-orange-800",
  partially_shipped: "bg-purple-100 text-purple-800",
  shipped: "bg-cyan-100 text-cyan-800",
  delivered: "bg-emerald-100 text-emerald-800",
  invoiced: "bg-indigo-100 text-indigo-800",
  paid: "bg-green-100 text-green-800",
  cancelled: "bg-slate-100 text-slate-600",
};

const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const prettyStatus = (s) => (s || "open").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const HospitalSupplierOrdersPanel = ({ token, hospitalId, hospitalName }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      if (!hospitalId && !hospitalName) return;
      setLoading(true);
      try {
        // Filter by hospital_id when known; fall back to client-side name match.
        const params = hospitalId ? { hospital_id: hospitalId } : {};
        const res = await axios.get(`${API}/api/admin/supplier-orders`, { headers, params });
        let list = res.data.orders || [];
        if (!hospitalId && hospitalName) {
          list = list.filter((o) => (o.hospital_name || "").toLowerCase() === hospitalName.toLowerCase());
        }
        setOrders(list);
      } catch (e) {
        console.error(e);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hospitalId, hospitalName, headers]);

  const emailOrderStatus = async (order) => {
    try {
      const r = await axios.get(`${API}/api/admin/supplier-orders/${order.id}/email-draft`, { headers });
      const { subject, body, contacts } = r.data;
      const primary = (contacts || []).find((c) => c.is_primary) || (contacts || [])[0];
      if (!primary) {
        toast.info("No saved contacts — opening Outlook with empty To: line. Add a contact for one-click email next time.");
      }
      const to = primary ? encodeURIComponent(primary.email) : "";
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } catch (e) {
      toast.error("Failed to draft email");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500" data-testid="hospital-supplier-orders-loading">
        Loading supplier orders...
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="hospital-supplier-orders-panel">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Supplier Orders</h3>
          <span className="text-xs text-slate-500 ml-2">{orders.length} linked</span>
        </div>
        <Link
          to="/admin/supplier-orders"
          className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
          data-testid="open-full-supplier-orders-tracker"
        >
          Open full tracker <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {orders.length === 0 ? (
        <div className="px-4 py-8 text-center text-slate-500 text-sm">
          No supplier orders linked to this hospital yet.{" "}
          <Link to="/admin/supplier-orders" className="text-blue-600 hover:underline">Add one →</Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hospital-supplier-orders-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">PO #</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-center">Aging</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{o.po_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{o.order_date || "—"}</td>
                  <td className="px-3 py-2 font-medium text-slate-700">{o.supplier_name}</td>
                  <td className="px-3 py-2 text-slate-700 max-w-xs truncate" title={o.description}>{o.description}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">{fmtMoney(o.ext_cost)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[o.status] || "bg-slate-100"}`}>
                      {prettyStatus(o.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {["delivered", "paid", "cancelled"].includes(o.status) ? (
                      <span className="text-slate-300 text-xs">—</span>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        o.aging_days > 14 ? "bg-red-100 text-red-800 font-bold" :
                        o.aging_days > 7 ? "bg-amber-100 text-amber-800" :
                        "bg-emerald-100 text-emerald-800"
                      }`}>{o.aging_days}d</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => emailOrderStatus(o)}
                      className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold"
                      data-testid={`hospital-email-status-${o.po_number}`}
                      title="Email status to hospital contact (opens Outlook)"
                    >
                      <Mail className="w-3 h-3" />
                      Email Status
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HospitalSupplierOrdersPanel;
